import type { LspClient } from "./lspClient.ts";
import type { LspTextEdit, LspWorkspaceEdit } from "./types.ts";
import { relativeFromSynthetic } from "./types.ts";

/** Everything the Monaco providers need about ONE Project's editor, supplied by the hook (kept free of `monaco`). */
export interface LspBinding {
  machineId: string;
  projectId: string;
  client: LspClient;
  /** Opens (as tabs) the Project files that do not have a model yet, so their edits can be applied as unsaved edits. */
  ensureOpen(paths: string[]): Promise<boolean>;
  /** Makes sure (possibly hidden, read-only-in-spirit) models exist so Monaco can show peek views of these files. */
  ensureModels(paths: string[]): Promise<void>;
  /** Applies text edits to the model of an open Project file as ONE undoable edit. */
  applyEdits(path: string, edits: LspTextEdit[]): boolean;
  /** Opens a Project file and reveals a range (Go to Definition / Problems). */
  reveal(path: string, range: { startLineNumber: number; startColumn: number; endLineNumber: number; endColumn: number }): void;
  report(message: string): void;
}

export class WorkspaceEditRejected extends Error {}

/** Flattens an LSP WorkspaceEdit into per-file edits. Rejects anything outside the Project and file operations. */
export function planWorkspaceEdit(projectId: string, edit: LspWorkspaceEdit): Map<string, LspTextEdit[]> {
  const out = new Map<string, LspTextEdit[]>();
  const add = (uri: string, edits: LspTextEdit[]) => {
    const path = relativeFromSynthetic(projectId, uri);
    if (path === null) throw new WorkspaceEditRejected("The edit touches a file outside this Project and was not applied.");
    out.set(path, [...(out.get(path) ?? []), ...edits]);
  };
  for (const [uri, edits] of Object.entries(edit.changes ?? {})) add(uri, edits);
  for (const change of edit.documentChanges ?? []) {
    if ("kind" in change) throw new WorkspaceEditRejected("File create/rename/delete edits are not supported yet and were not applied.");
    add(change.textDocument.uri, change.edits);
  }
  return out;
}

/** Applies a Project-bounded WorkspaceEdit to the open models (all-or-nothing validation up front). */
export async function applyWorkspaceEdit(binding: LspBinding, edit: LspWorkspaceEdit): Promise<boolean> {
  let plan: Map<string, LspTextEdit[]>;
  try {
    plan = planWorkspaceEdit(binding.projectId, edit);
  } catch (err) {
    if (err instanceof WorkspaceEditRejected) {
      binding.report(err.message);
      return false;
    }
    throw err;
  }
  if (plan.size === 0) return true;
  if (!(await binding.ensureOpen([...plan.keys()]))) {
    binding.report("Some files touched by the edit could not be opened; nothing was applied.");
    return false;
  }
  let all = true;
  for (const [path, edits] of plan) all = binding.applyEdits(path, edits) && all;
  return all;
}
